/**
 * Budget Assistant Schema
 *
 * Builds the "scope" (what Limitless AI is allowed to help with on Budget pages)
 * and the field schema (what fields exist, their labels/types/tooltips) that gets sent
 * to the server on every Limitless AI request while a Budget page is active.
 *
 * Keeping this generated from BUDGET_JSON_FIELD_CONFIGS ensures the assistant's
 * knowledge of fields never drifts from the real redux-form fields.
 */

import { getFieldsForContext } from './budgetJsonFieldConfigs';

export const BUDGET_ASSISTANT_SCOPE = 'budget-form';

/**
 * COMMON_FIELDS
 *
 * budgetJsonFieldConfigs.js only describes the platform-specific fields that get
 * serialized into the budget_json column (Google/Meta/TikTok/DSP targeting details).
 * It does NOT include the "always there" structural fields that are hardcoded directly
 * in GroupEditModalForm.jsx / CommonBudgetFieldsPart1.jsx / CommonBudgetFields.jsx /
 * ItemBudgetFields.jsx / BrandBudgetFields.jsx / DspBudgetFields.jsx /
 * AmazonKeywordFields.jsx.
 *
 * Without these, Limitless AI could only "see" the Meta/Google/etc-specific fields and
 * had no idea about Budget Group Name, Budget Type, Dates, Budget Driver, State, Item
 * Type/Selection, Negative/Custom Keywords, etc. This list fills that gap so the
 * assistant is grounded on the ENTIRE form, not just the platform-specific portion.
 *
 * `showForPlatforms`/`showForBudgetTypes` mirror the conditional rendering logic in the
 * actual form components, so the schema sent to the assistant matches what's really on
 * screen for the current platform/budget type.
 */
const COMMON_FIELDS = [
  // Always-present fields (top of form, before Budget Type selector)
  {
    name: 'budgetGroupName', label: 'Budget Group Name', type: 'text', tooltip: 'Case sensitive and must be unique', required: true, showForPlatforms: null, showForBudgetTypes: null,
  },
  {
    name: 'budgetType', label: 'Budget Type', type: 'select', tooltip: 'Choose the type of budget/ad campaign you want to create', required: true, showForPlatforms: null, showForBudgetTypes: null,
  },

  // Setup Control
  {
    name: 'setupFlow', label: 'Budget Group Setup Flow', type: 'select', tooltip: 'Quick Setup or Advanced Setup', required: false, showForPlatforms: null, showForBudgetTypes: null,
  },
  {
    name: 'agencyInternal', label: 'View Toggle', type: 'select', tooltip: 'Agency View or Limitless View (admin only)', required: false, showForPlatforms: null, showForBudgetTypes: null,
  },

  // Allowed days of the week (advanced mode)
  {
    name: 'monday', label: 'Allowed Day: Monday', type: 'checkbox', tooltip: 'Whether this budget group is allowed to spend on Mondays', required: false, showForPlatforms: null, showForBudgetTypes: null,
  },
  {
    name: 'tuesday', label: 'Allowed Day: Tuesday', type: 'checkbox', tooltip: 'Whether this budget group is allowed to spend on Tuesdays', required: false, showForPlatforms: null, showForBudgetTypes: null,
  },
  {
    name: 'wednesday', label: 'Allowed Day: Wednesday', type: 'checkbox', tooltip: 'Whether this budget group is allowed to spend on Wednesdays', required: false, showForPlatforms: null, showForBudgetTypes: null,
  },
  {
    name: 'thursday', label: 'Allowed Day: Thursday', type: 'checkbox', tooltip: 'Whether this budget group is allowed to spend on Thursdays', required: false, showForPlatforms: null, showForBudgetTypes: null,
  },
  {
    name: 'friday', label: 'Allowed Day: Friday', type: 'checkbox', tooltip: 'Whether this budget group is allowed to spend on Fridays', required: false, showForPlatforms: null, showForBudgetTypes: null,
  },
  {
    name: 'saturday', label: 'Allowed Day: Saturday', type: 'checkbox', tooltip: 'Whether this budget group is allowed to spend on Saturdays', required: false, showForPlatforms: null, showForBudgetTypes: null,
  },
  {
    name: 'sunday', label: 'Allowed Day: Sunday', type: 'checkbox', tooltip: 'Whether this budget group is allowed to spend on Sundays', required: false, showForPlatforms: null, showForBudgetTypes: null,
  },

  // Cost target / spend controls
  {
    name: 'costTargetType', label: 'Cost Target Type', type: 'select', tooltip: 'Blended or Brand/NonBrand cost targeting', required: false, showForPlatforms: null, showForBudgetTypes: null,
  },
  {
    name: 'costTarget', label: 'Cost Target', type: 'text', tooltip: 'The blended cost target (e.g. ROAS) for this budget group', required: false, showForPlatforms: null, showForBudgetTypes: null,
  },
  {
    name: 'costTargetNonBrand', label: 'Cost Target (Non-Brand)', type: 'text', tooltip: 'The non-brand cost target when using Brand/NonBrand cost targeting', required: false, showForPlatforms: null, showForBudgetTypes: null,
  },
  {
    name: 'spendSpeed', label: 'Spend Speed', type: 'select', tooltip: 'Standard, Fast, or Fastest spend speed (advanced + admin)', required: false, showForPlatforms: null, showForBudgetTypes: null,
  },
  {
    name: 'spendTactic', label: 'Spend Tactic', type: 'select', tooltip: 'Spend tactic strategy for this budget group (advanced + admin)', required: false, showForPlatforms: null, showForBudgetTypes: null,
  },
  {
    name: 'limitlessBudget', label: 'Limitless Budget', type: 'select', tooltip: 'Strict Budget, No Budget, or Flexible Budget (advanced + admin)', required: false, showForPlatforms: null, showForBudgetTypes: null,
  },
  {
    name: 'spendPriority', label: 'Spend Priority', type: 'select', tooltip: 'Priority given to Brand vs Non-Brand spend', required: false, showForPlatforms: null, showForBudgetTypes: null,
  },

  // Item/Other type fields (hidden for Google, brand, dsp)
  {
    name: 'itemType', label: 'Item Type', type: 'select', tooltip: 'SKU, ASIN, or ITEM (advanced + admin)', required: false, showForPlatforms: null, showForBudgetTypes: ['item', 'other'],
  },
  {
    name: 'itemSelection', label: 'Item Selection', type: 'select', tooltip: 'All or Top item selection (advanced + admin)', required: false, showForPlatforms: null, showForBudgetTypes: ['item', 'other', 'brand'],
  },
  {
    name: 'destination', label: 'Destination', type: 'select', tooltip: 'ON or OFF - describes where ad traffic is directed (advanced + admin)', required: false, showForPlatforms: null, showForBudgetTypes: ['item', 'other', 'brand', 'dsp'],
  },
  {
    name: 'masterItemOnList', label: 'ON Items/SKUs/ASINs', type: 'textarea', tooltip: 'Items in this list will have their Ad Status turned ON (comma separated)', required: false, showForPlatforms: null, showForBudgetTypes: ['item', 'other'],
  },
  {
    name: 'masterItemOffList', label: 'OFF Items/SKUs/ASINs', type: 'textarea', tooltip: 'Items in this list will have their Ad Status turned OFF (comma separated)', required: false, showForPlatforms: null, showForBudgetTypes: ['item', 'other'],
  },
  {
    name: 'splitItemsCheckbox', label: 'Split Items Into Individual Budget Groups', type: 'checkbox', tooltip: 'Create a Budget Group for each Item listed (advanced, new budgets only)', required: false, showForPlatforms: null, showForBudgetTypes: ['item', 'other'],
  },

  // Brand type fields
  {
    name: 'brand', label: 'Brand', type: 'select', tooltip: 'Select the brand for this budget group', required: false, showForPlatforms: null, showForBudgetTypes: ['brand'],
  },
  {
    name: 'brandAdFormat', label: 'Brand Ad Format', type: 'select', tooltip: 'Video, Product Collection, or Store Spotlight', required: false, showForPlatforms: null, showForBudgetTypes: ['brand'],
  },
  {
    name: 'masterItemOnList', label: 'Brand ASINs', type: 'textarea', tooltip: 'ASINs for this brand budget (must start with B0, 10 characters, 3-100 ASINs required)', required: true, showForPlatforms: null, showForBudgetTypes: ['brand'],
  },

  // DSP type fields
  {
    name: 'dspItemType', label: 'DSP Item Type', type: 'select', tooltip: 'Product Page, Custom Landing Page, Brand Collection, Product Collection, or Store', required: false, showForPlatforms: null, showForBudgetTypes: ['dsp'],
  },
  {
    name: 'dspAdFormat', label: 'DSP Ad Format', type: 'select', tooltip: 'Display or Video ad format', required: false, showForPlatforms: null, showForBudgetTypes: ['dsp'],
  },
  {
    name: 'dspGender', label: 'DSP Gender Targeting', type: 'select', tooltip: 'Male and Female, Male, or Female', required: false, showForPlatforms: null, showForBudgetTypes: ['dsp'],
  },
  {
    name: 'dspSpendTactic', label: 'DSP Spend Tactic', type: 'select', tooltip: 'Full Funnel, Top/Middle/Bottom of Funnel, or Loyalty Retargeting Funnel', required: false, showForPlatforms: null, showForBudgetTypes: ['dsp'],
  },
  {
    name: 'dspSpendSpeed', label: 'DSP Spend Speed', type: 'select', tooltip: 'Auto (ALL), AOO, APD, or 3PE', required: false, showForPlatforms: null, showForBudgetTypes: ['dsp'],
  },

  // Amazon Keyword Control (Amazon only, all budget types)
  {
    name: 'negativeKeywords', label: 'Negative Keywords', type: 'textarea', tooltip: 'Comma-separated negative keywords', required: false, showForPlatforms: ['amazon'], showForBudgetTypes: null,
  },
  {
    name: 'negativePhrase', label: 'Negative Phrase Match', type: 'checkbox', tooltip: 'Apply negative keywords as Phrase match', required: false, showForPlatforms: ['amazon'], showForBudgetTypes: null,
  },
  {
    name: 'negativeExact', label: 'Negative Exact Match', type: 'checkbox', tooltip: 'Apply negative keywords as Exact match', required: false, showForPlatforms: ['amazon'], showForBudgetTypes: null,
  },
  {
    name: 'customKeywords', label: 'Custom Keywords', type: 'textarea', tooltip: 'Comma-separated custom keywords', required: false, showForPlatforms: ['amazon'], showForBudgetTypes: null,
  },
  {
    name: 'customBroad', label: 'Custom Broad Match', type: 'checkbox', tooltip: 'Apply custom keywords as Broad match', required: false, showForPlatforms: ['amazon'], showForBudgetTypes: null,
  },
  {
    name: 'customPhrase', label: 'Custom Phrase Match', type: 'checkbox', tooltip: 'Apply custom keywords as Phrase match', required: false, showForPlatforms: ['amazon'], showForBudgetTypes: null,
  },
  {
    name: 'customExact', label: 'Custom Exact Match', type: 'checkbox', tooltip: 'Apply custom keywords as Exact match', required: false, showForPlatforms: ['amazon'], showForBudgetTypes: null,
  },

  // Dates, Budget Driver, Amounts, State (always present, at the end of the form)
  {
    name: 'startDate', label: 'Start Date', type: 'date', tooltip: 'When this budget group starts', required: true, showForPlatforms: null, showForBudgetTypes: null,
  },
  {
    name: 'endDate', label: 'End Date', type: 'date', tooltip: 'When this budget group ends (optional)', required: false, showForPlatforms: null, showForBudgetTypes: null,
  },
  {
    name: 'budgetDriver', label: 'Budget Driver', type: 'select', tooltip: 'Daily or Monthly budget driver', required: true, showForPlatforms: null, showForBudgetTypes: null,
  },
  {
    name: 'budgetAllocationSpSd', label: 'Budget Allocation: SP + SD', type: 'number', tooltip: 'Percent of budget allocated to Sponsored Products + Sponsored Display (Amazon only, item type)', required: false, showForPlatforms: ['amazon'], showForBudgetTypes: ['item'],
  },
  {
    name: 'budgetAllocationSb', label: 'Budget Allocation: SB', type: 'number', tooltip: 'Percent of budget allocated to Sponsored Brands (Amazon only, item type)', required: false, showForPlatforms: ['amazon'], showForBudgetTypes: ['item'],
  },
  {
    name: 'budgetAllocationSt', label: 'Budget Allocation: ST', type: 'number', tooltip: 'Percent of budget allocated to Sponsored Television (Amazon only, item type)', required: false, showForPlatforms: ['amazon'], showForBudgetTypes: ['item'],
  },
  {
    name: 'baseDailyBudgetAmount', label: 'Daily Budget Amount', type: 'number', tooltip: 'Daily budget amount in account currency (when Budget Driver is Daily)', required: true, showForPlatforms: null, showForBudgetTypes: null,
  },
  {
    name: 'january', label: 'Monthly Budget: January', type: 'number', tooltip: 'Budget amount for January (when Budget Driver is Monthly)', required: false, showForPlatforms: null, showForBudgetTypes: null,
  },
  {
    name: 'february', label: 'Monthly Budget: February', type: 'number', tooltip: 'Budget amount for February (when Budget Driver is Monthly)', required: false, showForPlatforms: null, showForBudgetTypes: null,
  },
  {
    name: 'march', label: 'Monthly Budget: March', type: 'number', tooltip: 'Budget amount for March (when Budget Driver is Monthly)', required: false, showForPlatforms: null, showForBudgetTypes: null,
  },
  {
    name: 'april', label: 'Monthly Budget: April', type: 'number', tooltip: 'Budget amount for April (when Budget Driver is Monthly)', required: false, showForPlatforms: null, showForBudgetTypes: null,
  },
  {
    name: 'may', label: 'Monthly Budget: May', type: 'number', tooltip: 'Budget amount for May (when Budget Driver is Monthly)', required: false, showForPlatforms: null, showForBudgetTypes: null,
  },
  {
    name: 'june', label: 'Monthly Budget: June', type: 'number', tooltip: 'Budget amount for June (when Budget Driver is Monthly)', required: false, showForPlatforms: null, showForBudgetTypes: null,
  },
  {
    name: 'july', label: 'Monthly Budget: July', type: 'number', tooltip: 'Budget amount for July (when Budget Driver is Monthly)', required: false, showForPlatforms: null, showForBudgetTypes: null,
  },
  {
    name: 'august', label: 'Monthly Budget: August', type: 'number', tooltip: 'Budget amount for August (when Budget Driver is Monthly)', required: false, showForPlatforms: null, showForBudgetTypes: null,
  },
  {
    name: 'september', label: 'Monthly Budget: September', type: 'number', tooltip: 'Budget amount for September (when Budget Driver is Monthly)', required: false, showForPlatforms: null, showForBudgetTypes: null,
  },
  {
    name: 'october', label: 'Monthly Budget: October', type: 'number', tooltip: 'Budget amount for October (when Budget Driver is Monthly)', required: false, showForPlatforms: null, showForBudgetTypes: null,
  },
  {
    name: 'november', label: 'Monthly Budget: November', type: 'number', tooltip: 'Budget amount for November (when Budget Driver is Monthly)', required: false, showForPlatforms: null, showForBudgetTypes: null,
  },
  {
    name: 'december', label: 'Monthly Budget: December', type: 'number', tooltip: 'Budget amount for December (when Budget Driver is Monthly)', required: false, showForPlatforms: null, showForBudgetTypes: null,
  },
  {
    name: 'reallocation', label: 'Total Daily Reallocation Budget Amount', type: 'number', tooltip: 'Reallocation budget amount (daily + advanced + admin)', required: false, showForPlatforms: null, showForBudgetTypes: null,
  },
  {
    name: 'state', label: 'Budget Group State', type: 'select', tooltip: 'Enabled, Disabled, Draft, or Completed', required: true, showForPlatforms: null, showForBudgetTypes: null,
  },
];

/**
 * Resolves the internal budgetType value (e.g. 'meta_facebook') down to the shared
 * "family" value (item/brand/dsp/other) used by showForBudgetTypes filters above, since
 * Google/Meta/TikTok ad types map to families like 'item'/'other' for common-field
 * visibility (they all behave like "item" budgets structurally).
 */
function resolveBudgetTypeFamily(platform, budgetType) {
  if (['item', 'brand', 'dsp', 'other'].includes(budgetType)) {
    return budgetType;
  }
  if (['google', 'meta', 'tiktok'].includes(platform)) {
    return 'item';
  }
  return budgetType;
}

/**
 * Builds the list of common/structural fields relevant to the given platform/budgetType
 */
function getCommonFieldSchema(platform, budgetType) {
  const budgetTypeFamily = resolveBudgetTypeFamily(platform, budgetType);
  return COMMON_FIELDS
    .filter(field => (!field.showForPlatforms || field.showForPlatforms.includes(platform)))
    .filter(field => (!field.showForBudgetTypes || field.showForBudgetTypes.includes(budgetTypeFamily)))
    .map(field => ({
      name: field.name, label: field.label, type: field.type, tooltip: field.tooltip, required: !!field.required,
    }));
}

export const BUDGET_ASSISTANT_SCOPE_DESCRIPTION = 'Help the user with anything on this Budget Management page. '
  + 'This page has multiple tabs: Account (retailer/portfolio settings), Groups (create/edit '
  + 'budget groups for Meta, Google, DSP, TikTok, Walmart, Amazon), Events (scheduled budget '
  + 'events), Items (item-level budget management), Growth (budget growth planning), and '
  + 'Forecast (budget forecasting/reporting). The current active tab is provided in '
  + 'context.activeTab. On the Groups tab, you can fill out form fields directly using '
  + 'context.fieldSchema and offer to open "Add Budget Group" (only on this tab). On other '
  + 'tabs there is no fillable form - instead, inspect whatever data/context is provided and '
  + 'help the user understand it, answer questions about it, or explain what actions are '
  + 'available (e.g. explaining a chart, a table column, or how to use a feature on that tab). '
  + 'Only answer questions related to this Budget Management page and its tabs. '
  + 'Do not answer unrelated general questions.';

/**
 * Returns the scope + scopeDescription for the Budget Management pages
 * @returns {{scope: string, scopeDescription: string}}
 */
export function getBudgetAssistantScope() {
  return {
    scope: BUDGET_ASSISTANT_SCOPE,
    scopeDescription: BUDGET_ASSISTANT_SCOPE_DESCRIPTION,
  };
}

/**
 * Builds a compact field schema for the given platform/budgetType/setupFlow context.
 *
 * Merges TWO sources so Limitless AI can see the WHOLE form, not just the
 * platform-specific portion:
 *   1. Common/structural fields (Budget Group Name, Budget Type, Dates, Budget Driver,
 *      State, Item/Brand/DSP type fields, Amazon Keyword Control, etc.) - see COMMON_FIELDS.
 *   2. Platform-specific budget_json fields (Google/Meta/TikTok/DSP targeting details)
 *      from budgetJsonFieldConfigs.js via getFieldsForContext().
 *
 * @param {string} platform - Platform key (e.g., 'google', 'meta')
 * @param {string} budgetType - Budget type (e.g., 'google_search', 'dsp', 'item')
 * @param {string} setupFlow - Setup flow ('quick' or 'advanced')
 * @returns {Array} Array of { name, label, type, tooltip, required } objects
 */
export function getBudgetAssistantFieldSchema(platform, budgetType, setupFlow) {
  const commonFields = getCommonFieldSchema(platform, budgetType);
  const platformSpecificFields = getFieldsForContext(platform, budgetType, setupFlow).map(field => ({
    name: field.name,
    label: field.label,
    type: field.type,
    tooltip: field.tooltip,
    required: !!field.required,
  }));

  // Merge, de-duplicating by field name (first occurrence wins if there's ever overlap;
  // platform-specific fields are listed first since they're more specific to the context)
  const seen = new Set();
  const merged = [];
  [...platformSpecificFields, ...commonFields].forEach(field => {
    if (!seen.has(field.name)) {
      seen.add(field.name);
      merged.push(field);
    }
  });
  return merged;
}

export default {
  getBudgetAssistantScope,
  getBudgetAssistantFieldSchema,
};
